import React, { createContext, useContext, useState, ReactNode, useMemo, useEffect } from 'react'
import Fuse from 'fuse.js'
import debounce from 'lodash.debounce'
import { Alert, useApplicationSuiteContext } from '../../../contexts/ApplicationSuiteProvider'
import StandardAlertTemplate from '../types/StandardAlertTemplate'
import { useCollectionData } from 'react-firebase-hooks/firestore'
import { collection, query, where, orderBy } from 'firebase/firestore'
import { firebase_firestore } from '../../../common/firebaseConfig'
import { Box } from '@mui/material'
import SidePanel from '../components/SidePanel'
import TemplateSidePanel from '../components/TemplateSidePanel'
import { useTheme } from '@mui/material/styles'
import AlertEditView from '../components/AlertEditView'
import { useTutorialContext } from '../../../contexts/TutorialContext'
import { createTutorialMockAlert } from '../../../tour/TutorialMockData'
import { AlertTradeSummary, useAlertTradeSummaries } from '../hooks/useAlertTradeSummaries'
import { useTemplateCards } from '../hooks/useTemplateCards'

// Side panel types
export type SidePanelTab = 'settings' | 'history' | 'conditions'

interface AlertContextType {
  alerts: Alert[]
  setAlerts: React.Dispatch<React.SetStateAction<Alert[]>>
  filteredAlerts: Alert[]
  searchTerm: string
  setSearchTerm: (term: string) => void
  selectedAlert: Alert | null
  setSelectedAlert: (alert: Alert | null) => void
  isSidePanelOpen: boolean
  openSidePanel: (alert: Alert) => void
  closeSidePanel: () => void
  activeSidePanelTab: SidePanelTab
  setActiveSidePanelTab: (tab: SidePanelTab) => void
  isEditMode: boolean
  editingAlertId: string | null
  enterEditMode: (alertId: string) => void
  exitEditMode: () => void
  scrollToSelectedAlert: (alert: Alert) => void
  pendingScrollAlertId: string | null
  setPendingScrollAlertId: React.Dispatch<React.SetStateAction<string | null>>
  loading: boolean
  error: Error | undefined
  viewMode: 'alerts' | 'templates'
  setViewMode: (mode: 'alerts' | 'templates') => void
  selectedTemplate: StandardAlertTemplate | null
  isCreatingTemplate: boolean
  isTemplateSidePanelOpen: boolean
  openTemplateSidePanel: (template: StandardAlertTemplate | null) => void
  closeTemplateSidePanel: () => void
  // Batched per-alert trade stats for auto-trade cards, keyed by alert id
  tradeSummaries: Record<string, AlertTradeSummary>
  tradeSummariesLoading: boolean
  refreshTradeSummaries: () => void
  // Standard alert templates the member has no alert for yet, rendered as cards
  templateCards: ReturnType<typeof useTemplateCards>['templateCards']
  // True while the offer is on but its listeners haven't delivered yet
  templateCardsLoading: boolean
  // Resolves true once the member has the alert (created now or already there)
  createFromTemplate: (template: StandardAlertTemplate) => Promise<boolean>
}

const AlertContext = createContext<AlertContextType | undefined>(undefined)

export const useAlertContext = () => {
  const context = useContext(AlertContext)
  if (context === undefined) {
    throw new Error('useAlertContext must be used within an AlertProvider')
  }
  return context
}

interface AlertProviderProps {
  children: ReactNode
  isNested?: boolean
  parentTab?: number
  // Set only by the provider that renders AlertCardList, the sole consumer of template
  // cards. Every other provider (the builder's, ShareableDialog's) skips the templates
  // listener entirely.
  offerTemplates?: boolean
}

export const AlertProvider: React.FC<AlertProviderProps> = ({
  children,
  isNested,
  parentTab,
  offerTemplates = false,
}) => {
  const theme = useTheme()

  const { tradingHouse, usermeta, activeTab } = useApplicationSuiteContext()

  const { isTutorialActive, activeTutorialType } = useTutorialContext()

  const [alerts, setAlerts] = useState<Alert[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedAlert, setSelectedAlert] = useState<Alert | null>(null)
  const [isSidePanelOpen, setIsSidePanelOpen] = useState(false)
  const [activeSidePanelTab, setActiveSidePanelTab] = useState<SidePanelTab>('settings')
  const [isEditMode, setIsEditMode] = useState(false)
  const [editingAlertId, setEditingAlertId] = useState<string | null>(null)
  const [pendingScrollAlertId, setPendingScrollAlertId] = useState<string | null>(null)
  const [viewMode, setViewMode] = useState<'alerts' | 'templates'>('alerts')
  const [selectedTemplate, setSelectedTemplate] = useState<StandardAlertTemplate | null>(null)
  const [isCreatingTemplate, setIsCreatingTemplate] = useState(false)
  const [isTemplateSidePanelOpen, setIsTemplateSidePanelOpen] = useState(false)

  // Fetch alerts from Firebase
  const [data, loading, error] = useCollectionData<Alert | any>(
    query(
      collection(firebase_firestore, 'tradingPlans'),
      where('user.gcid', '==', usermeta.gcid),
      where('pattern.tradingHouse', '==', tradingHouse),
      orderBy('active', 'desc')
    )
  )

  // Update alerts in context when data changes
  useEffect(() => {
    if (data) {
      let updatedAlerts = [...data]

      // Remove exit trading plans from the list of alerts
      updatedAlerts = updatedAlerts.filter(
        (alert) => !alert.alertTypes?.includes('exitTradingPlan')
      )

      // Add tutorial demo alert when alerts tutorial is active
      if (isTutorialActive && activeTutorialType === 'alerts') {
        const tutorialDemoAlert = createTutorialMockAlert(usermeta, tradingHouse)
        updatedAlerts = [tutorialDemoAlert, ...updatedAlerts]
      }

      setAlerts(updatedAlerts)
    }
  }, [data, isTutorialActive, activeTutorialType, usermeta, tradingHouse])

  // Only auto-trade alerts have trades to summarize
  const autoTradeAlertIds = useMemo(
    () => alerts.filter((alert) => !!alert.trading?.accountId).map((alert) => alert.id),
    [alerts]
  )
  const {
    summaries: tradeSummaries,
    loading: tradeSummariesLoading,
    refresh: refreshTradeSummaries,
  } = useAlertTradeSummaries(autoTradeAlertIds)

  // Template cards never enter `alerts`, so no alert cap or auto-trader count sees them.
  // Suppressed during the alerts tutorial, whose steps target the first card in the DOM,
  // and in the admin templates view, which renders its own table.
  const { templateCards, templateCardsLoading, createFromTemplate } = useTemplateCards({
    // The query result, not the `alerts` state: that state is filled by an effect a frame
    // later, so reading it here would paint one frame of cards for alerts already owned.
    alerts: data ?? [],
    searchTerm,
    // Every tab mounts at app load, so without the tab check each provider would hold its
    // own listener for the whole session.
    enabled:
      offerTemplates &&
      activeTab.tab === parentTab &&
      !loading &&
      viewMode === 'alerts' &&
      !(isTutorialActive && activeTutorialType === 'alerts'),
    onAlertCreated: setPendingScrollAlertId,
  })

  // Configure Fuse instance
  const fuse = useMemo(
    () =>
      new Fuse(alerts, {
        keys: ['name', 'pattern.name', 'pattern.trigger.chartDetails.symbol'],
        threshold: 0.3,
        ignoreLocation: true,
      }),
    [alerts]
  )

  // Compute filtered alerts based on search term
  const [filteredAlerts, setFilteredAlerts] = useState<Alert[]>(alerts)

  // Create a stable reference to the debounced search function
  const debouncedSearch = useMemo(() => {
    return debounce((searchValue: string) => {
      const results = fuse.search(searchValue).map((result) => result.item)
      setFilteredAlerts(results)
    }, 200)
  }, [fuse])

  // Update filtered alerts when search term changes
  useEffect(() => {
    // Cancel any pending debounced searches
    debouncedSearch.cancel()

    // If empty, immediately update
    if (!searchTerm.trim()) {
      setFilteredAlerts(alerts)
      return
    }

    // Otherwise, debounce the search
    debouncedSearch(searchTerm)

    // Cleanup function to cancel pending searches
    return () => {
      debouncedSearch.cancel()
    }
  }, [searchTerm, debouncedSearch, alerts])

  // Cleanup debounced function on unmount
  useEffect(() => {
    return () => {
      debouncedSearch.cancel()
    }
  }, [debouncedSearch])

  useEffect(() => {
    closeSidePanel()
  }, [tradingHouse])

  // A newly created alert can't be scrolled to until the Firestore listener has
  // delivered it and its card has rendered — scrollToSelectedAlert resolves the
  // element by data-alert-id. Park the id and fire once the alert actually lands.
  useEffect(() => {
    if (!pendingScrollAlertId) return
    const newAlert = alerts.find((alert) => alert.id === pendingScrollAlertId)
    if (!newAlert) return
    scrollToSelectedAlert(newAlert)
    setPendingScrollAlertId(null)
  }, [alerts, pendingScrollAlertId])

  useEffect(() => {
    if (!usermeta?.isAdmin && viewMode === 'templates') {
      setViewMode('alerts')
    }
  }, [usermeta?.isAdmin, viewMode])

  const openSidePanel = (alert: Alert) => {
    setSelectedAlert(alert)
    setIsSidePanelOpen(true)
    setTimeout(() => {
      scrollToSelectedAlert(alert)
    }, 250)
  }

  const closeSidePanel = () => {
    setIsSidePanelOpen(false)
    // We don't immediately clear the selected alert to allow for exit animations
    // It will be cleared after the animation completes
  }

  const openTemplateSidePanel = (template: StandardAlertTemplate | null) => {
    setSelectedTemplate(template)
    setIsCreatingTemplate(template === null)
    setIsTemplateSidePanelOpen(true)
  }

  const closeTemplateSidePanel = () => {
    setIsTemplateSidePanelOpen(false)
  }

  const enterEditMode = (alertId: string) => {
    setEditingAlertId(alertId)
    setIsEditMode(true)
    // Close the side panel if it's open
    if (isSidePanelOpen) {
      closeSidePanel()
    }
  }

  const exitEditMode = () => {
    setIsEditMode(false)
    setEditingAlertId(null)
  }

  const scrollToSelectedAlert = (alert: Alert) => {
    if (alert) {
      // Find the alert card element by class and ID
      setTimeout(() => {
        const alertCardElement = document.querySelector(`.alert-card[data-alert-id="${alert.id}"]`)
        const containerElement = document.getElementById('alert-card-list-container')

        if (alertCardElement && containerElement) {
          // Get the position of the alert card relative to the container
          const containerRect = containerElement.getBoundingClientRect()
          const alertRect = alertCardElement.getBoundingClientRect()

          // Calculate the scroll position
          const scrollTop =
            alertRect.top -
            containerRect.top +
            containerElement.scrollTop -
            containerRect.height / 2 +
            alertRect.height / 2

          // Scroll the container to the alert card
          containerElement.scrollTo({
            top: scrollTop,
            behavior: 'smooth',
          })

          // Highlight the alert card briefly
          alertCardElement.classList.add('highlight-alert')
          setTimeout(() => {
            alertCardElement.classList.remove('highlight-alert')
          }, 1500)
        }
      }, 100) // Small delay to ensure the DOM has updated
    }
  }

  const value = {
    alerts,
    setAlerts,
    filteredAlerts,
    searchTerm,
    setSearchTerm,
    selectedAlert,
    setSelectedAlert,
    isSidePanelOpen,
    setIsSidePanelOpen,
    activeSidePanelTab,
    setActiveSidePanelTab,
    isEditMode,
    setIsEditMode,
    editingAlertId,
    enterEditMode,
    exitEditMode,
    scrollToSelectedAlert,
    pendingScrollAlertId,
    setPendingScrollAlertId,
    openSidePanel,
    closeSidePanel,
    loading,
    error,
    viewMode,
    setViewMode,
    selectedTemplate,
    isCreatingTemplate,
    isTemplateSidePanelOpen,
    openTemplateSidePanel,
    closeTemplateSidePanel,
    tradeSummaries,
    tradeSummariesLoading,
    refreshTradeSummaries,
    templateCards,
    templateCardsLoading,
    createFromTemplate,
  }

  return (
    <AlertContext.Provider value={value}>
      {/* Main content container */}
      <Box
        sx={{
          display: 'flex',
          height: '100%',
          width: '100%',
          overflow: 'hidden',
          position: 'relative',
          flexDirection: 'column',
        }}
      >
        {!isEditMode ? (
          <>
            <Box
              sx={{
                display: 'flex',
                flex: 1,
                overflow: 'hidden',
              }}
            >
              {/* Main content area */}
              <Box
                sx={{
                  overflowY: 'auto',
                  flex: '1 1 auto',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                }}
              >
                {children}
              </Box>

              {/* Side Panel container */}
              <Box
                sx={{
                  flexShrink: 0,
                  overflowY: 'auto',
                  background: theme.palette.background.paper,
                  borderLeft: `1px solid ${theme.palette.divider}`,
                  width: isSidePanelOpen || isTemplateSidePanelOpen ? '402px' : '0px',
                  transition: 'width 0.3s ease',
                  position: isNested ? 'absolute' : 'relative',
                  top: 0,
                  right: 0,
                  bottom: 0,
                }}
              >
                {activeTab.tab === parentTab &&
                  (viewMode === 'templates' ? <TemplateSidePanel /> : <SidePanel />)}
              </Box>
            </Box>
          </>
        ) : (
          <AlertEditView />
        )}
      </Box>
    </AlertContext.Provider>
  )
}
